import React, { useRef, useEffect } from 'react';
import { Clock, Calendar, MapPin } from 'lucide-react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';

export default function TodayShows({ shows }) {
  const scrollRef = useRef(null);

  if (!shows || shows.length === 0) return null;

  // Auto-scroll effect
  useEffect(() => {
    if (!shows || shows.length <= 1) return;
    const interval = setInterval(() => {
      if (scrollRef.current) {
        const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
        if (scrollLeft + clientWidth >= scrollWidth - 10) {
          scrollRef.current.scrollTo({ left: 0, behavior: 'smooth' });
        } else {
          scrollRef.current.scrollBy({ left: 320, behavior: 'smooth' });
        }
      }
    }, 4000);
    return () => clearInterval(interval);
  }, [shows]);

  // Helper to ensure valid image URL from backend relative or absolute paths
  const getImageUrl = (img) => {
    if (!img) return 'https://upload.wikimedia.org/wikipedia/commons/4/41/Placeholder_view_vector.svg';
    if (img.startsWith('http://') || img.startsWith('https://')) return img;
    if (img.startsWith('/')) return img;
    return '/' + img;
  };

  const formatTime = (timeStr) => {
    if (!timeStr) return '11:00 PM';
    if (timeStr.toLowerCase().includes('am') || timeStr.toLowerCase().includes('pm')) return timeStr;
    const [h, m] = timeStr.split(':');
    if (h && m) {
      const hour = parseInt(h, 10);
      const ampm = hour >= 12 ? 'PM' : 'AM';
      const formattedHour = hour % 12 || 12;
      return `${formattedHour}:${m} ${ampm}`;
    }
    return timeStr;
  };

  return (
    <motion.div 
      initial={{ opacity: 0 }}
      whileInView={{ opacity: 1 }}
      viewport={{ once: true, amount: 0.2 }}
      transition={{ duration: 0.5, ease: "easeOut" }}
      className="max-w-7xl mx-auto px-2 sm:px-6 lg:px-8 mt-4 md:mt-6 mb-6"
    >
      {/* Header */}
      <div className="flex items-center mb-3 md:mb-6 px-1 sm:px-0">
        <div className="h-5 w-1 bg-[#d33a33] rounded-full mr-2"></div>
        <Clock size={18} className="text-[#d33a33] mr-2 shrink-0" />
        <h2 className="text-base md:text-xl font-bold text-gray-900 mr-2.5">Today Show</h2>
        <span className="bg-[#ffebec] text-[#d33a33] text-[9px] font-bold px-2 py-0.5 rounded-full flex items-center border border-[#ffd2d4]">
          <span className="w-1.5 h-1.5 bg-[#d33a33] rounded-full mr-1 animate-pulse"></span>
          LIVE
        </span>
      </div>

      {/* Horizontal Scroll / Slider */}
      <div 
        ref={scrollRef}
        className="flex overflow-x-auto gap-2.5 sm:gap-3 md:gap-4 pb-4 [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none] snap-x snap-mandatory"
      >
        {shows.map((show, index) => {
          const rawDate = show.jatra_date || show.date || show.camp_from;
          const dateObj = rawDate ? new Date(rawDate) : null;
          const displayDate = dateObj && !isNaN(dateObj) 
            ? dateObj.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })
            : (rawDate || 'Today');

          const timeText = formatTime(show.jatra_time || show.time);
          const dateTimeString = `${displayDate} • ${timeText}`;

          const showTitle = show.jatra_name || show.product_name || show.title || 'Jatra Show';
          const partyName = show.cat_name || show.jatra_party || 'Gananatya';
          const locationText = show.jatra_location || show.district_name || show.place_name || show.location || 'Odisha';
          const imgSrc = getImageUrl(show.image || show.product_image);
          const showId = show.product_id || show.id || index;

          return (
            <motion.div 
              key={showId}
              whileHover={{ y: -3 }}
              transition={{ duration: 0.2 }}
              className="w-[85vw] sm:w-[310px] md:w-[330px] max-w-[330px] bg-white rounded-2xl border border-gray-100 p-2 sm:p-3 flex gap-2 sm:gap-3 shadow-xs hover:shadow-md transition-all shrink-0 snap-start group cursor-pointer"
            >
              {/* Poster Image */}
              <div className="w-24 md:w-28 h-32 md:h-36 rounded-xl bg-gray-100 overflow-hidden shrink-0 relative">
                <img 
                  src={imgSrc} 
                  alt={showTitle} 
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  onError={(e) => {
                    e.target.src = 'https://upload.wikimedia.org/wikipedia/commons/4/41/Placeholder_view_vector.svg';
                  }}
                />
                <span className="absolute top-1.5 left-1.5 bg-red-600 text-white text-[8px] font-bold px-1.5 py-0.5 rounded shadow-xs">
                  TODAY
                </span>
              </div>

              {/* Show Details */}
              <div className="flex-1 flex flex-col justify-between py-0.5 min-w-0">
                <div className="min-w-0">
                  <span className="text-[10px] font-bold text-red-500 uppercase tracking-wider block truncate">
                    {partyName}
                  </span>
                  <h3 className="text-xs sm:text-sm font-bold text-gray-900 truncate mt-0.5 group-hover:text-red-600 transition-colors" title={showTitle}>
                    {showTitle}
                  </h3>

                  <div className="mt-2 space-y-1 min-w-0">
                    <p className="text-[11px] sm:text-xs text-gray-500 flex items-center gap-1.5 min-w-0">
                      <Calendar size={12} className="text-red-500 shrink-0" />
                      <span className="truncate">{dateTimeString}</span>
                    </p>
                    <p className="text-[11px] sm:text-xs text-gray-500 flex items-center gap-1.5 min-w-0" title={locationText}>
                      <MapPin size={12} className="text-blue-500 shrink-0" />
                      <span className="truncate block">{locationText}</span>
                    </p>
                  </div>
                </div>

                <Link 
                  to={`/shows/${showId}`}
                  className="w-full bg-red-50 hover:bg-red-600 text-red-600 hover:text-white text-center text-xs font-bold py-1.5 rounded-lg transition-colors block mt-2"
                >
                  Book Tickets
                </Link>
              </div>
            </motion.div>
          );
        })}
      </div>
    </motion.div>
  );
}
